import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CenteredSkeleton, FieldSkeleton } from "../../components/skeletons.jsx";
import { useI18n } from "../../i18n/index.js";
import { authClient } from "../../lib/auth-client.js";
import { invalidateSession } from "../../lib/session.js";
import { Brand, Button, CenteredPage, FormError, Skeleton, TextField } from "../../ui/index.js";

/** While a fresh load finds out whether someone is already signed in (and so leaves this page). */
export function SignUpSkeleton() {
  return (
    <CenteredSkeleton>
      <div className="dt-form">
        <FieldSkeleton label={<Skeleton width={48} />} />
        <FieldSkeleton label={<Skeleton width={48} />} />
        <FieldSkeleton label={<Skeleton width={72} />} />
        <Skeleton shape="button" width="100%" />
      </div>
    </CenteredSkeleton>
  );
}

export function SignUpRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t, tNode } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = params.get("next") ?? "/";

  return (
    <CenteredPage>
      <div className="dt-centered__head">
        <Brand size="lg" />
        <div>
          <h1>{t("signUp.title")}</h1>
          <p className="dt-muted">{t("signUp.subtitle")}</p>
        </div>
      </div>
      <form
        className="dt-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          const form = new FormData(e.currentTarget);
          const { error } = await authClient.signUp.email({
            name: String(form.get("name")),
            email: String(form.get("email")),
            password: String(form.get("password")),
          });
          setBusy(false);
          if (error) setError(error.message ?? t("signUp.failed"));
          else {
            // This page's middleware cached "signed out"; drop it or the next
            // navigation would bounce straight back here.
            invalidateSession();
            navigate(next.startsWith("/") ? next : "/");
          }
        }}
      >
        <TextField name="name" label={t("field.name")} placeholder={t("signUp.namePlaceholder")} required autoComplete="name" />
        <TextField name="email" label={t("field.email")} type="email" placeholder={t("field.emailPlaceholder")} required autoComplete="email" />
        <TextField name="password" label={t("field.password")} type="password" description={t("signUp.passwordHint")} minLength={10} required autoComplete="new-password" />
        <FormError message={error} />
        <Button type="submit" variant="primary" loading={busy}>
          {t("signUp.submit")}
        </Button>
      </form>
      <p className="dt-centered__foot">
        {tNode("signUp.haveAccount", { link: <Link to={`/sign-in?next=${encodeURIComponent(next)}`}>{t("signUp.signInLink")}</Link> })}
      </p>
    </CenteredPage>
  );
}
